import React, { useState, useEffect, useCallback } from 'react';
import { Search, Eye, CheckCircle, XCircle, MessageSquare, Filter } from 'lucide-react';
import { CoachProfile } from '../../types';
import { getAllCoachProfilesForQueue } from '../../services/firebase';
import { Link } from 'react-router-dom';

const VerificationQueue: React.FC = () => {
  const [coaches, setCoaches] = useState<CoachProfile[]>([]);
  const [filteredCoaches, setFilteredCoaches] = useState<CoachProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const coachesPerPage = 10;
  const [statusFilter, setStatusFilter] = useState<'all' | 'pending' | 'approved' | 'rejected'>('all');

  const loadCoaches = async () => {
    try {
      console.log('VerificationQueue: Loading all coaches...');
      const allCoaches = await getAllCoachProfilesForQueue();
      console.log('VerificationQueue: Received coaches:', allCoaches);
      setCoaches(allCoaches);
      setFilteredCoaches(allCoaches);
    } catch (error) {
      console.error('Error loading coaches:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = useCallback((term: string) => {
    if (term.length < 2) {
      // If search term is too short, apply status filter only
      if (statusFilter === 'all') {
        setFilteredCoaches(coaches);
      } else {
        const filtered = coaches.filter(coach => coach.verificationStatus === statusFilter);
        setFilteredCoaches(filtered);
      }
      setCurrentPage(1);
      return;
    }

    // Search through all coaches (or filtered by status)
    const searchableCoaches = statusFilter === 'all' 
      ? coaches 
      : coaches.filter(coach => coach.verificationStatus === statusFilter);

    const searchResults = searchableCoaches.filter(coach => 
      coach.firstName.toLowerCase().includes(term.toLowerCase()) ||
      coach.lastName.toLowerCase().includes(term.toLowerCase()) ||
      coach.email.toLowerCase().includes(term.toLowerCase()) ||
      coach.baseballOrganization.toLowerCase().includes(term.toLowerCase()) ||
      coach.baseballTeam.toLowerCase().includes(term.toLowerCase())
    );
    
    setFilteredCoaches(searchResults);
    setCurrentPage(1);
  }, [coaches, statusFilter]);

  const handleStatusFilter = useCallback((status: 'all' | 'pending' | 'approved' | 'rejected') => {
    setStatusFilter(status);
    
    // Apply both status filter and search term
    let filtered = coaches;
    
    // First apply status filter
    if (status !== 'all') {
      filtered = coaches.filter(coach => coach.verificationStatus === status);
    }
    
    // Then apply search filter if there's a search term
    if (searchTerm.length >= 2) {
      filtered = filtered.filter(coach => 
        coach.firstName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        coach.lastName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        coach.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        coach.baseballOrganization.toLowerCase().includes(searchTerm.toLowerCase()) ||
        coach.baseballTeam.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    setFilteredCoaches(filtered);
    setCurrentPage(1);
  }, [coaches, searchTerm]);

  useEffect(() => {
    loadCoaches();
  }, []);

  useEffect(() => {
    handleSearch(searchTerm);
  }, [searchTerm, handleSearch]);

  const formatDate = (timestamp: any) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
  };

  const getStatusBadge = (status: string) => {
    const baseClasses = "px-2 py-1 rounded-full text-xs font-medium";
    switch (status) {
      case 'pending':
        return `${baseClasses} bg-yellow-100 text-yellow-800`;
      case 'approved':
        return `${baseClasses} bg-green-100 text-green-800`;
      case 'rejected':
        return `${baseClasses} bg-red-100 text-red-800`;
      default:
        return `${baseClasses} bg-gray-100 text-gray-800`;
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pending':
        return <MessageSquare className="h-4 w-4 text-yellow-600" />;
      case 'approved':
        return <CheckCircle className="h-4 w-4 text-green-600" />;
      case 'rejected':
        return <XCircle className="h-4 w-4 text-red-600" />;
      default:
        return <MessageSquare className="h-4 w-4 text-gray-600" />;
    }
  };

  // Pagination
  const indexOfLastCoach = currentPage * coachesPerPage;
  const indexOfFirstCoach = indexOfLastCoach - coachesPerPage;
  const currentCoaches = filteredCoaches.slice(indexOfFirstCoach, indexOfLastCoach);
  const totalPages = Math.ceil(filteredCoaches.length / coachesPerPage);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-dark"></div>
      </div>
    );
  }

  return (
    <div className="card">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-900">Coach Verifications</h2>
        <div className="flex items-center space-x-4">
          {/* Status Filter */}
          <div className="flex items-center space-x-2">
            <Filter className="h-4 w-4 text-gray-500" />
            <select
              value={statusFilter}
              onChange={(e) => handleStatusFilter(e.target.value as any)}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-dark"
            >
              <option value="all">All Status</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>
          
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search coaches..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-dark text-sm"
            />
          </div>
        </div>
      </div>

      {filteredCoaches.length === 0 ? (
        <div className="text-center py-12">
          <p className="text-gray-500 text-lg">No coaches found</p>
          <p className="text-sm text-gray-400 mt-2">
            Debug: Total coaches loaded: {coaches.length}
          </p>
        </div>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Coach
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Organization
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Submitted
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {currentCoaches.map((coach) => (
                  <tr key={coach.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {coach.firstName} {coach.lastName}
                        </div>
                        <div className="text-sm text-gray-500">{coach.email}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{coach.baseballOrganization}</div>
                      <div className="text-sm text-gray-500">{coach.baseballTeam}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center space-x-2">
                        {getStatusIcon(coach.verificationStatus)}
                        <span className={getStatusBadge(coach.verificationStatus)}>
                          {coach.verificationStatus.charAt(0).toUpperCase() + coach.verificationStatus.slice(1)}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(coach.verificationSubmittedAt)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <Link
                        to={`/coach/${coach.id}`}
                        className="btn-primary flex items-center space-x-2"
                      >
                        <Eye className="h-4 w-4" />
                        <span>View Details</span>
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex justify-between items-center mt-6">
              <div className="text-sm text-gray-700">
                Showing {indexOfFirstCoach + 1} to {Math.min(indexOfLastCoach, filteredCoaches.length)} of {filteredCoaches.length} results
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <span className="px-3 py-2 text-sm text-gray-700">
                  Page {currentPage} of {totalPages}
                </span>
                <button
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages}
                  className="px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default VerificationQueue; 